// next-app/src/lib/utils.ts

import {
  EFGEventStatus,
  type EFGEvent,
  type EligibilityCustomData,
  type EventCustomData,
} from '@/types';
import { clsx, type ClassValue } from 'clsx';
import { addDays, format, isValid, parseISO, startOfDay } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { twMerge } from 'tailwind-merge';

// ============================================
// SHADCN/UI UTILITY (from Phase 01)
// ============================================

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// ============================================
// DATE UTILITIES
// ============================================

/**
 * Parse a date-only string (e.g. "2025-01-15") into a Date set to noon.
 * Avoids timezone shift where UTC midnight rolls back a day in US timezones.
 */
export function parseDate(date: string | Date): Date {
  const d = typeof date === 'string' ? parseISO(date) : new Date(date);
  d.setHours(12, 0, 0, 0);
  return d;
}

/**
 * Format a date for display in event grids.
 * Replaces: moment(date).format("ddd MMM Do yyyy")
 * Output: "Mon Jan 1st 2023"
 */
export function formatEventDate(date: Date | string | null | undefined): string {
  if (!date) return '';

  const dateObj = parseDate(date);
  if (!isValid(dateObj)) return '';

  // date-fns doesn't have ordinal suffix built-in, so we add it manually
  const day = dateObj.getDate();
  const suffix = getOrdinalSuffix(day);

  return format(dateObj, `EEE MMM d'${suffix}' yyyy`);
}

/**
 * Get ordinal suffix for a day number (1st, 2nd, 3rd, 4th, etc.)
 */
export function getOrdinalSuffix(day: number): string {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}

/**
 * Format a date for API submission.
 * Replaces: moment(date).format('YYYY-MM-DD')
 */
export function formatDateForAPI(date: Date | null | undefined): string {
  if (!date || !isValid(date)) return '';
  return format(date, 'yyyy-MM-dd');
}

/**
 * Format a time for API submission.
 * Replaces: moment(time).format('HH:mm:00')
 */
export function formatTimeForAPI(time: Date | null | undefined): string {
  if (!time || !isValid(time)) return '';
  return format(time, 'HH:mm:00');
}

/**
 * Parse a date string from the API.
 * Returns null if parsing fails.
 */
export function parseDateFromAPI(dateString: string | null | undefined): Date | null {
  if (!dateString) return null;
  const date = parseISO(dateString);
  return isValid(date) ? date : null;
}

/**
 * Parse a time string (HH:mm:ss) into a Date object.
 * Uses today's date as the base.
 */
export function parseTimeFromAPI(timeString: string | null | undefined): Date | null {
  if (!timeString) return null;

  const parts = timeString.split(':').map(Number);
  const hours = parts[0];
  const minutes = parts[1];
  const seconds = parts[2];

  if (hours === undefined || minutes === undefined || isNaN(hours) || isNaN(minutes)) return null;

  const date = new Date();
  date.setHours(hours, minutes, seconds ?? 0, 0);
  return date;
}

/**
 * Format a date with timezone consideration.
 * Used for displaying event times in their local timezone.
 */
export function formatDateInTimezone(
  date: Date | string,
  timezone: string,
  formatStr: string = 'yyyy-MM-dd HH:mm:ss'
): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  if (!isValid(dateObj)) return '';

  try {
    return formatInTimeZone(dateObj, timezone, formatStr);
  } catch {
    // Fallback to local time if timezone is invalid
    return format(dateObj, formatStr);
  }
}

/**
 * Calculate minimum eligible date for event requests.
 * Rules: max(seasonStart, today + 21 days) for eligibility
 *        max(seasonStart, today + 60 days) for planning
 */
export function getMinEventDate(seasonStartDate: Date, daysFromNow: number = 21): Date {
  const today = startOfDay(new Date());
  const minFromToday = addDays(today, daysFromNow);

  return minFromToday > seasonStartDate ? minFromToday : seasonStartDate;
}

// ============================================
// STATUS UTILITIES
// ============================================

/**
 * Status sort order mapping for grid display.
 * Lower numbers appear first in sorted lists.
 *
 * Priority order:
 * 0 - new (needs attention)
 * 1 - review (awaiting approval)
 * 2 - eligible (ready to plan)
 * 3 - planning (in progress)
 * 4-7 - approved through event ended (chronological)
 * 99+ - terminal states (ineligible, revise, cancelled)
 */
export const STATUS_SORT_ORDER: Record<EFGEventStatus, number> = {
  [EFGEventStatus.new]: 0,
  [EFGEventStatus.review]: 1,
  [EFGEventStatus.eligible]: 2,
  [EFGEventStatus.planning]: 3,
  [EFGEventStatus.approved]: 4,
  [EFGEventStatus['in progress']]: 5,
  [EFGEventStatus['event ending']]: 6,
  [EFGEventStatus['event ended']]: 7,
  [EFGEventStatus['donation approved']]: 8,
  [EFGEventStatus.reimbursement_requested]: 9,
  [EFGEventStatus.reimbursement_approved]: 10,
  [EFGEventStatus.ineligible]: 99,
  [EFGEventStatus.revise]: 100,
  [EFGEventStatus.cancelled]: 101,
};

/**
 * Get the sort order for a status string.
 * Returns 999 for unknown statuses.
 */
export function getStatusSortOrder(status: EFGEventStatus | string | undefined): number {
  if (!status) return 999;
  return STATUS_SORT_ORDER[status as EFGEventStatus] ?? 999;
}

/**
 * Process events list to add computed sort fields.
 * This replaces the inline processing in Events.tsx.
 */
export function processEventsForDisplay(events: EFGEvent[]): EFGEvent[] {
  return events.map((event) => {
    const processed = { ...event };

    // Add sort_status
    processed.sort_status = getStatusSortOrder(event.status);

    // Determine sort_date priority: start_date > requested_date > parsed from custom data
    if (event.start_date) {
      processed.sort_date =
        typeof event.start_date === 'string' ? parseISO(event.start_date) : event.start_date;
    } else if (event.requested_date) {
      processed.sort_date =
        typeof event.requested_date === 'string'
          ? parseISO(event.requested_date)
          : event.requested_date;
    } else if (event.eligibility_custom_data) {
      const customData = parseEligibilityCustomData(event.eligibility_custom_data);
      if (customData?.requested_date) {
        processed.sort_date = customData.requested_date;
      }
    }

    // Format display_date
    if (processed.sort_date) {
      processed.display_date = formatEventDate(processed.sort_date);
    }

    return processed;
  });
}

/**
 * Check if an event status allows editing.
 */
export function isEditableStatus(status: EFGEventStatus | string | undefined): boolean {
  if (!status) return false;
  return [EFGEventStatus.eligible, EFGEventStatus.planning, EFGEventStatus.revise].includes(
    status as EFGEventStatus
  );
}

/**
 * Check if an event status allows portal access.
 */
export function hasPortalAccess(status: EFGEventStatus | string | undefined): boolean {
  if (!status) return false;
  return [
    EFGEventStatus.approved,
    EFGEventStatus['in progress'],
    EFGEventStatus['event ending'],
    EFGEventStatus['event ended'],
  ].includes(status as EFGEventStatus);
}

/**
 * Check if an event can request reimbursement.
 */
export function canRequestReimbursement(event: EFGEvent): boolean {
  return event.status === EFGEventStatus['donation approved'] && !event.reimbursement_requested;
}

// ============================================
// JSON PARSING UTILITIES
// ============================================

/**
 * Safely parse eligibility_custom_data JSON field.
 * Returns null if parsing fails.
 */
export function parseEligibilityCustomData(
  jsonString: string | null | undefined
): EligibilityCustomData | null {
  if (!jsonString) return null;

  try {
    const parsed = JSON.parse(jsonString);

    // Convert date string to Date object if present
    if (parsed.requested_date && typeof parsed.requested_date === 'string') {
      parsed.requested_date = parseISO(parsed.requested_date);
    }

    return parsed as EligibilityCustomData;
  } catch {
    console.warn('Failed to parse eligibility_custom_data:', jsonString);
    return null;
  }
}

/**
 * Safely parse event_custom_data JSON field.
 * Returns null if parsing fails.
 */
export function parseEventCustomData(
  jsonString: string | null | undefined
): EventCustomData | null {
  if (!jsonString) return null;

  try {
    return JSON.parse(jsonString) as EventCustomData;
  } catch {
    console.warn('Failed to parse event_custom_data:', jsonString);
    return null;
  }
}

/**
 * Stringify custom data for API submission.
 * Ensures consistent JSON formatting.
 */
export function stringifyCustomData<T>(data: T): string {
  return JSON.stringify(data);
}
